import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  FormControlLabel,
  Paper
} from '@mui/material';
import {
  Description as DocumentIcon,
  Psychology as AIIcon,
  CheckCircleOutline as SuccessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

import { apiClient } from '@/services/api';

interface ProjectInfo {
  id: string;
  project_name: string;
}

interface DocumentInfo {
  filename: string;
  size: number;
  modified_time: string;
}

interface DepartmentIndicatorAIDialogProps {
  open: boolean;
  onClose: () => void;
  project: ProjectInfo | null;
  onRecommendationComplete: (result: any) => void;
}

export default function DepartmentIndicatorAIDialog({
  open,
  onClose,
  project,
  onRecommendationComplete
}: DepartmentIndicatorAIDialogProps) {
  // 状态管理
  const [step, setStep] = useState(0); // 0: 选择文档, 1: 推荐中, 2: 显示结果
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<any>(null);
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([]);

  // 当对话框打开时，重置状态
  useEffect(() => {
    if (!open) {
      setStep(0);
      setDocuments([]);
      setSelectedDocuments([]);
      setLoading(false);
      setError(null);
      setRecommendation(null);
      setSelectedIndicators([]);
    } else {
      fetchDocuments();
    }
  }, [open, project]);

  // 监听全局文档刷新事件
  useEffect(() => {
    const handleGlobalRefresh = () => {
      if (open) {
        fetchDocuments();
      }
    };

    window.addEventListener('documentsShouldRefresh', handleGlobalRefresh);
    return () => {
      window.removeEventListener('documentsShouldRefresh', handleGlobalRefresh);
    };
  }, [open]);

  // 获取文档列表
  const fetchDocuments = async () => {
    if (!project) return;
    
    setError(null);
    
    try {
      console.log('开始获取文档列表...');
      const responseData = await apiClient.get(`/projects/${project.id}/department-indicators/ai/documents`);
      console.log('ApiClient返回数据:', responseData);
      
      if (!responseData) {
        throw new Error('API响应为空');
      }
      
      if (responseData.success !== true) {
        throw new Error(`API响应success为false或不存在: ${responseData.success}`);
      }
      
      if (!responseData.data || !responseData.data.documents) {
        throw new Error('API响应中缺少data.documents字段');
      }
      
      const documents = responseData.data.documents;
      console.log('文档列表:', documents);
      
      // 过滤出markdown和文本文档
      const textDocs = documents.filter((doc: DocumentInfo) => 
        doc.filename.toLowerCase().endsWith('.md') || 
        doc.filename.toLowerCase().endsWith('.txt') ||
        doc.filename.includes('MD_') ||
        doc.filename.includes('TXT_') ||
        doc.filename.includes('DOCX_')
      );
      
      setDocuments(textDocs);
      
      if (textDocs.length === 0) {
        throw new Error('请先上传项目资料文档，或等待文档解析完毕（状态变为"已完成"）后再使用AI推荐功能');
      }
      
      console.log(`获取到 ${textDocs.length} 个文档`);
      
    } catch (err: any) {
      console.error('获取文档列表失败:', err);
      setError(`文档数据获取错误: ${err.message || '网络错误'}`);
    }
  };

  // 处理文档选择
  const handleDocumentToggle = (document: DocumentInfo) => {
    setSelectedDocuments(prev => {
      const isSelected = prev.some(doc => doc.filename === document.filename);
      if (isSelected) {
        return prev.filter(doc => doc.filename !== document.filename);
      } else {
        return [...prev, document];
      }
    });
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments([...documents]);
    }
  };

  // 开始推荐指标
  const handleStartRecommendation = async () => {
    if (!project) {
      setError('项目信息未加载，请稍后重试');
      return;
    }
    
    if (selectedDocuments.length === 0) {
      setError('请至少选择一个文档');
      return;
    }
    
    setStep(1);
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post(`/projects/${project.id}/department-indicators/ai/recommend`, {
        project_name: project.project_name,
        selected_documents: selectedDocuments.map(doc => doc.filename),
      }, { timeout: 180000 }); // 3分钟超时
      
      if (response && response.success) {
        setRecommendation(response);
        // 自动选中所有推荐的指标
        const allIndicatorNames = response.data.recommendedIndicators.map((indicator: any) => 
          `${indicator.level1_name}-${indicator.level2_name}-${indicator.level3_name}`
        );
        setSelectedIndicators(allIndicatorNames);
        setStep(2);
      } else {
        throw new Error(response?.error || '推荐结果格式错误');
      }
    } catch (err: any) {
      console.error('推荐指标失败:', err);
      setError(err.message);
      setStep(0);
    } finally {
      setLoading(false);
    }
  };

  // 处理指标选择
  const handleIndicatorSelection = (indicatorKey: string, checked: boolean) => {
    if (checked) {
      setSelectedIndicators(prev => [...prev, indicatorKey]);
    } else {
      setSelectedIndicators(prev => prev.filter(key => key !== indicatorKey));
    }
  };

  // 处理全选
  const handleSelectAllIndicators = (checked: boolean) => {
    if (checked && recommendation) {
      const allIndicatorNames = recommendation.data.recommendedIndicators.map((indicator: any) => 
        `${indicator.level1_name}-${indicator.level2_name}-${indicator.level3_name}`
      );
      setSelectedIndicators(allIndicatorNames);
    } else {
      setSelectedIndicators([]);
    }
  };

  // 确认并应用选中的指标
  const handleConfirm = () => {
    if (!recommendation || selectedIndicators.length === 0) {
      setError('请至少选择一个指标');
      return;
    }

    // 过滤出被选中的指标
    const filteredIndicators = recommendation.data.recommendedIndicators.filter((indicator: any) => {
      const key = `${indicator.level1_name}-${indicator.level2_name}-${indicator.level3_name}`;
      return selectedIndicators.includes(key);
    });

    // 构造返回结果
    const result = {
      ...recommendation,
      data: {
        ...recommendation.data,
        recommendedIndicators: filteredIndicators,
        totalIndicatorCount: filteredIndicators.length
      }
    };

    onRecommendationComplete(result);
    onClose();
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 按一二级指标分组
  const groupedIndicators = recommendation?.data.recommendedIndicators.reduce((acc: any, indicator: any) => {
    const level1 = indicator.level1_name;
    const level2 = indicator.level2_name;
    
    if (!acc[level1]) {
      acc[level1] = {};
    }
    if (!acc[level1][level2]) {
      acc[level1][level2] = [];
    }
    acc[level1][level2].push(indicator);
    
    return acc;
  }, {}) || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AIIcon color="primary" />
          <Typography variant="h6">AI智能推荐部门指标</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 步骤 0: 选择文档 */}
        {step === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>AI推荐说明：</strong>
                <br />• <strong>投入管理指标</strong>：已预设固定内容（工作目标管理、预算和财务管理、绩效管理），共20个指标，无需AI推荐
                <br />• <strong>产出指标和效益管理指标</strong>：AI将根据您选择的项目文档进行推荐
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              请选择用于分析的项目文档，AI将基于这些文档推荐产出和效益指标。
            </Typography>

            {documents.length === 0 ? (
              <Alert severity="info">
                当前项目没有可用的文档。请先上传相关文档后再使用AI推荐功能。
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">
                    已选择 {selectedDocuments.length} / {documents.length} 个文档
                  </Typography>
                  <Button size="small" onClick={handleSelectAll}>
                    {selectedDocuments.length === documents.length ? '取消全选' : '全选'}
                  </Button>
                </Box>

                <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <List>
                    {documents.map((doc, index) => {
                      const isSelected = selectedDocuments.some(d => d.filename === doc.filename);
                      return (
                        <ListItem
                          key={index}
                          button
                          onClick={() => handleDocumentToggle(doc)}
                          selected={isSelected}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.filename}
                            secondary={`${formatFileSize(doc.size)} • ${new Date(doc.modified_time).toLocaleString('zh-CN')}`}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              </>
            )}
          </Box>
        )}

        {/* 步骤 1: 推荐中 */}
        {step === 1 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <AIIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              AI正在分析文档并推荐指标...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              这可能需要1-2分钟，请稍候
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* 步骤 2: 显示推荐结果 */}
        {step === 2 && recommendation && (
          <Box>
            <Alert severity="success" icon={<SuccessIcon />} sx={{ mb: 2 }}>
              AI已成功推荐 {recommendation.data.totalIndicatorCount} 个部门指标！
              {recommendation.data.fixedInputCount && recommendation.data.aiRecommendedCount && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  包含：{recommendation.data.fixedInputCount} 个固定投入管理指标 + {recommendation.data.aiRecommendedCount} 个AI推荐的产出/效益指标
                </Typography>
              )}
            </Alert>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>说明：</strong>
                <br />• <strong>投入管理指标</strong>：包括工作目标管理、预算和财务管理、绩效管理等，共20个指标，内容已固定
                <br />• <strong>产出指标和效益管理指标</strong>：根据项目文档由AI推荐生成
              </Typography>
            </Alert>

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2">
                已选择 {selectedIndicators.length} / {recommendation.data.totalIndicatorCount} 个指标
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedIndicators.length === recommendation.data.totalIndicatorCount}
                    onChange={(e) => handleSelectAllIndicators(e.target.checked)}
                  />
                }
                label="全选"
              />
            </Box>

            <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto' }}>
              {Object.entries(groupedIndicators).map(([level1, level2Groups]: [string, any]) => (
                <Accordion key={level1} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {level1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.entries(level2Groups).map(([level2, indicators]: [string, any]) => (
                      <Box key={level2} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          {level2}
                        </Typography>
                        <List dense>
                          {indicators.map((indicator: any, idx: number) => {
                            const indicatorKey = `${indicator.level1_name}-${indicator.level2_name}-${indicator.level3_name}`;
                            const isSelected = selectedIndicators.includes(indicatorKey);
                            
                            return (
                              <ListItem key={idx} sx={{ pl: 4 }}>
                                <ListItemIcon>
                                  <Checkbox
                                    edge="start"
                                    checked={isSelected}
                                    onChange={(e) => handleIndicatorSelection(indicatorKey, e.target.checked)}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {indicator.level3_name}
                                      </Typography>
                                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                        <Chip label={`指标值: ${indicator.indicator_value}`} size="small" variant="outlined" />
                                      </Stack>
                                    </Box>
                                  }
                                  secondary={
                                    <Typography variant="caption" color="text.secondary">
                                      {indicator.value_description}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        {step === 0 && (
          <Button
            variant="contained"
            onClick={handleStartRecommendation}
            disabled={selectedDocuments.length === 0 || loading}
          >
            开始AI推荐
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={selectedIndicators.length === 0}
          >
            确认应用 ({selectedIndicators.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

